import {
  Button,
  FieldSwitch,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { unwrapResult } from '@reduxjs/toolkit';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import { electron } from 'lib-shared-electron';
import {
  LinkedConversationTypes,
  MessagetextContentType,
  Queue,
  Tag,
} from 'lib-shared-legacy-types';
import { compareSemVer } from 'lib-shared-semver';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Field, FieldArray, InjectedFormProps, reduxForm } from 'redux-form';
import sanitizeHtml from 'sanitize-html';

import { LanguageCode } from 'app/utils/supportedLanguages';
import { EditorCopilotDropdown } from 'components/editorCopilotDropdown';
import { SatisfactionButton } from 'components/shared/satisfactionButton/SatisfactionButton';
import { SatisfactionDisabledButton } from 'components/shared/satisfactionButton/components/SatisfactionDisabledButton';
import { ValidationState } from 'components/shared/util/richTextField/components/components/stateBar/stateBar.types';
import { QueueSelectField } from 'fable/queueSelect';
import { FieldToggleBoxHead, ToggleBox, ToggleBoxBody } from 'fable/toggleBox';
import { getEmailIntegrationLabel } from 'utils/emailIntegration';
import { ServerStorageProps } from 'utils/useServerStorage';

import { getSubject } from '../../../../app/utils/conversationTitle';
import { getMessagesTranscript } from '../../../../app/utils/messages';
import InputField from '../../util/InputField';
import SelectField from '../../util/SelectField';
import { TagsSelectFieldArray } from '../../util/TagsSelectFieldArray';
import { getRequestID } from '../../util/request';
import RichTextField from '../../util/richTextField/containers/richTextField';
import AttachmentsFieldList from '../../util/richTextField/containers/utils/attachmentsFieldList';
import ListUserEmailOptions from '../../util/userField/ListUserEmailOptions';
import {
  EmailAddressWithOptionalName,
  EmailPayload,
  SaveDraftPayload,
  SendEmailResponseT,
} from '../api/composeEmail';
import ComposeEmailHelper from '../helper/ComposeEmailHelper';
import styles from '../styles/composeEmail.module.scss';

import {
  ComposeEmailModalErrorType,
  DispatchProps,
  StateProps,
} from './ComposeEmailModalContainer';
import { asyncValidate, validate } from './ComposeEmailModalValidation';
import ComposeWindowPortal from './ComposeWindowPortal';
import UserItem from './UserItem';

import '../styles/compose-email.less';

/** The redux-form name. */
export const FORM_NAME = 'composeEmailModal';

/** The electron version supporting pop-outs */
const DESKTOP_APP_SUPPPORT_POP_OUT_VERSION = '3.3.11';

/** The email user. */
export type EmailUser = { user: User & { display_name?: string | null } };

/** The data bound to the redux-form. */
export interface ComposeEmailModalFormData {
  parentConversation?: number;
  toUser?: EmailUser[];
  subject?: string;
  message?: string;
  attachments?: any[];
  to?: EmailUser[];
  cc?: EmailUser[];
  bcc?: EmailUser[];
  fromEmails?: string;
  linkConversationType?: LinkedConversationTypes;
  messageContentType?: MessagetextContentType;
  timestamp?: any;
  tags?: Tag[];
  queue?: Queue;
  offer?: boolean;
  internalNoteOpen?: boolean;
}

export type ComposeEmailModalProps = StateProps &
  DispatchProps &
  ServerStorageProps<'organization', 'organizationStorage'>;

type ComposeEmailModalState = {
  ccVisible: boolean;
  bccVisible: boolean;
  isSubjectLoading: boolean;
  showWindowPortal: boolean;
  userFieldFilled: boolean;
  supportsPopoutCompose: boolean;
  cancelledSatisfaction: boolean;
  satisfactionLanguage?: LanguageCode;
  messageContent: string;
  internalNoteContent: string;
  validation: ValidationState;
  users: [];
  allEmailsUsers: (User & { duplicate?: boolean })[];
};

type ComposeEmailModalPropsWithReduxForm = InjectedFormProps<
  ComposeEmailModalFormData,
  ComposeEmailModalProps,
  ComposeEmailModalErrorType
> &
  ComposeEmailModalProps;

const satisfactionLanguageFallback = 'en';

const INITIAL_STATE: ComposeEmailModalState = {
  ccVisible: true,
  bccVisible: true,
  isSubjectLoading: false,
  userFieldFilled: false,
  showWindowPortal: false,
  supportsPopoutCompose: true,
  cancelledSatisfaction: false,
  messageContent: '',
  internalNoteContent: '',
  validation: { touched: false, valid: false },
  users: [],
  allEmailsUsers: [],
};

/** Gets the list of recipients (cc & bcc) */
const getRecipients = (
  emailUsers: EmailUser[],
): EmailAddressWithOptionalName[] => {
  const recipients: EmailAddressWithOptionalName[] = [];

  for (const { user } of emailUsers) {
    const recipient: EmailAddressWithOptionalName = {
      email: user.email as string,
    };
    if (
      user.display_name &&
      typeof user.display_name === 'string' &&
      user.display_name.trim() !== ''
    ) {
      recipient.name = user.display_name;
    }
    recipients.push(recipient);
  }

  return recipients;
};

const getEmailPayload = ({
  to_email,
  message,
  internalNote,
  content_type,
  formData,
  language,
}: {
  to_email: EmailPayload['to_email'];
  message: EmailPayload['body'];
  internalNote: EmailPayload['internalNote'];
  content_type: EmailPayload['content_type'];
  formData: ComposeEmailModalFormData;
  language: LanguageCode | undefined;
}) => {
  const payload: EmailPayload = {
    from_email: formData.fromEmails as string,
    to_email,
    subject: formData.subject as string,
    body: message,
    content_type,
  };

  if (formData.cc && formData.cc.length > 0) {
    payload.cc = getRecipients(formData.cc);
  }
  if (formData.bcc && formData.bcc.length > 0) {
    payload.bcc = getRecipients(formData.bcc);
  }
  if (formData.tags && formData.tags.length > 0) {
    payload.tags = formData.tags;
  }
  if (formData.queue) {
    payload.queueId = formData.queue.id;
  }
  if (formData.offer !== undefined) {
    payload.offer = formData.offer;
  }
  if (language) {
    payload.language = language;
  }
  if (formData.parentConversation) {
    payload.parentConversation = formData.parentConversation;
  }
  if (formData.linkConversationType) {
    payload.linkType = formData.linkConversationType;
  }

  if (internalNote && formData.internalNoteOpen) {
    payload.internalNote = internalNote;
  }

  if (
    formData.attachments !== undefined &&
    Array.isArray(formData.attachments) === true
  ) {
    const attachments: EmailPayload['attachments_v2'] = [];
    for (const attachment of formData.attachments) {
      if (attachment !== undefined) {
        attachments.push({
          pretty_name: attachment.name,
          url: attachment.relative_uri,
        });
      }
    }
    payload.attachments_v2 = attachments;
  }

  return payload;
};

export class ComposeEmailModal extends React.Component<
  ComposeEmailModalPropsWithReduxForm,
  ComposeEmailModalState
> {
  private messageEditorRef: React.RefObject<any>;
  private internalNoteEditorRef: React.RefObject<any>;

  constructor(props) {
    super(props);
    this.state = INITIAL_STATE;
    this.messageEditorRef = React.createRef();
    this.internalNoteEditorRef = React.createRef();
  }

  async componentDidMount() {
    window.addEventListener('unload', this.handleUnload);

    if (electron.isElectron()) {
      const version = await electron.getVersion();
      const supportsPopoutCompose =
        compareSemVer(version, DESKTOP_APP_SUPPPORT_POP_OUT_VERSION) !== -1;
      this.setState({ supportsPopoutCompose });
    }
  }

  componentDidUpdate(prevProps: ComposeEmailModalProps) {
    const { ui, initialValues } = this.props;
    const { messageContent, internalNoteContent } = this.state;

    if (!ui.isModalOpen && messageContent !== '') {
      this.setState({
        messageContent: '',
      });
    }

    if (!ui.isModalOpen && internalNoteContent !== '') {
      this.setState({
        internalNoteContent: '',
      });
    }

    if (prevProps.ui.isModalOpen === false && ui.isModalOpen === true) {
      this.props.composeEmailGetAllFrom(null);
      this.setState({
        userFieldFilled: !!initialValues?.toUser,
      });
      if (this.props.fromEmails.length === 1) {
        this.props.change('fromEmails', this.props.fromEmails[0].email_address);
      }
      if (initialValues && initialValues.message) {
        /**
         * Delays setting the initial value as it would otherwise be overwritten
         * when the editor mounts. When the editor mounts/unmounts the DOM in
         * response to switching between `modal-mode` and `window-mode`, the
         * editor content is stored in a component state variable as it would
         * otherwise become lost. The mount/unmount hooks synchronizes the
         * current value of the editor with the state variable. When the modal
         * is first opened, the state variable is empty, which in turn sets the
         * content to empty string. Then when the timeout elapses the proper
         * initial value gets set. Note that we cannot update the state in
         * `componentDidUpdate`.
         */
        setTimeout(() => {
          this.setMessageEditorText(
            initialValues.message,
            initialValues.messageContentType === 'markdown',
          );
        }, 500);
      }
      if (initialValues && initialValues.internalNote) {
        this.props.change('internalNoteOpen', true);
        setTimeout(() => {
          this.setInternalNoteEditorText(initialValues.internalNote);
        }, 500);
      }
      if (initialValues && initialValues.fromEmails) {
        this.props.change('fromEmails', initialValues.fromEmails);
      }

      if (initialValues?.cancelledSatisfaction) {
        this.setState({
          cancelledSatisfaction: initialValues.cancelledSatisfaction,
        });
      }

      this.setState({
        satisfactionLanguage:
          initialValues.satisfactionLanguage ||
          this.props.metaConversation?.conversation?.attributes?.language,
      });
    }
  }

  static getDerivedStateFromProps(props, state: ComposeEmailModalState) {
    if (
      props.users !== state.users &&
      (props.users.length !== state.users.length ||
        props.users.some(
          (nextUser: User) =>
            !state.users.find((user: User) => nextUser.id === user.id),
        ))
    ) {
      return {
        users: props.users,
        allEmailsUsers: props.users.flatMap((user) => {
          if (user.additional_emails) {
            return [
              user,
              ...user.additional_emails.map((email) => ({
                ...user,
                email,
                duplicate: true,
              })),
            ];
          }

          return user;
        }),
      };
    }

    return null;
  }

  @boundMethod
  handleUnload() {
    this.closeWindowPortal();
  }

  messageSent = (
    { data: { csid: newConversationId } }: SendEmailResponseT,
    parentConversation: ComposeEmailModalFormData['parentConversation'],
  ) => {
    const notificationID = getRequestID();
    this.setValidationState({ touched: false, valid: false });

    const leftAction = {
      text: 'Go to conversation',
      action: {
        navigate: `/conversation/${newConversationId}`,
      },
    };
    const rightAction = {
      text: 'Dismiss',
      action: () => {
        this.props.notificationRemoveById({ id: notificationID }); // Remove the notification.
      },
    };

    const notificationOptions = {
      id: notificationID,
      type: 'messageSent',
      title: 'New message sent',
      text: `Message was sent`,
      icon: 'i-done',
      className: 'success',
      time: 10000,
      showDesktop: false,
      leftAction: leftAction,
      rightAction: rightAction,
    };

    if (
      this.props.initialValues !== null &&
      this.props.initialValues.timestamp != null
    ) {
      // We need to delete the old draft.
      this.props.composeEmailRemoveDraft({
        timestamp: this.props.initialValues.timestamp,
      });
    }

    this.props.notificationAdd(notificationOptions);

    // We make the new conversation a child conversation to a parent conversation.
    if (parentConversation) {
      this.props.conversationLinkChild({
        csid: newConversationId,
      });
    }

    this.props.destroy();
    this.props.composeEmailCloseModal();
  };

  getInternalNoteEditorText() {
    if (this.internalNoteEditorRef && this.internalNoteEditorRef.current) {
      const { current: newEditor } = this.internalNoteEditorRef;
      return newEditor.getText();
    }

    return '';
  }

  setInternalNoteEditorText(newText: string) {
    if (this.internalNoteEditorRef && this.internalNoteEditorRef.current) {
      this.internalNoteEditorRef.current.setText(newText);
    }
  }

  getMessageEditorText(asMarkdown = false) {
    if (this.messageEditorRef && this.messageEditorRef.current) {
      const { current: newEditor } = this.messageEditorRef;
      if (asMarkdown) {
        return newEditor.getMarkdown();
      }

      return newEditor.getText();
    }

    return '';
  }

  setMessageEditorText(newText: string, isMarkdown = false) {
    if (this.messageEditorRef && this.messageEditorRef.current) {
      if (isMarkdown) {
        this.messageEditorRef.current.setMarkdown(newText);
      } else {
        this.messageEditorRef.current.setText(newText);
      }
    }
  }

  handleSaveClick = (formData: ComposeEmailModalFormData) => {
    const { hasFFWriteHTMLEmail } = this.props;
    const message = this.getMessageEditorText(!hasFFWriteHTMLEmail);
    const internalNote = this.getInternalNoteEditorText();
    const content_type: MessagetextContentType = hasFFWriteHTMLEmail
      ? 'html'
      : null;

    if (!message || message.length < 1) {
      this.setValidationState({
        touched: true,
        valid: false,
        error: { type: 'required' },
      });
      return;
    }

    const language: LanguageCode | undefined = this.state.cancelledSatisfaction
      ? undefined
      : this.state.satisfactionLanguage;

    const showErrorNotification = (email: string, e: Error) => {
      const notificationOptions = {
        type: 'messageNotSent',
        title: 'An error occurred',
        text: `Some error occurred - message was not sent to ${email}`,
        icon: 'i-exclamation',
        className: 'warning',
        showDesktop: false,
      };
      this.props.notificationAdd(notificationOptions);
    };

    // Do we need to add the user before we sent the message.
    if (formData.toUser?.[0]?.user?.id) {
      const payload = getEmailPayload({
        to_email: formData.toUser[0].user.email,
        message,
        internalNote,
        content_type,
        formData,
        language,
      });
      this.setValidationState({ touched: true, valid: true });
      this.props
        .sendEmail(payload)
        .then((response: SendEmailResponseT) => {
          this.messageSent(response, formData.parentConversation);
          this.closeWindowPortal();
        })
        .catch((e: any) => {
          showErrorNotification(formData.toUser?.[0].user.email as string, e);
          console.info(e.data);
          console.warn('Add user exception', e);
        });
    } else {
      // We do not have the user id for this user.
      // We need to create the user.
      const user: any = {
        email: formData.toUser?.[0].user.email,
        roles: ['enduser'],
      };

      if (formData.toUser?.[0].user.display_name) {
        user.name = formData.toUser[0].user.display_name;
      }

      // Create user.
      this.props
        .userModalAddUser(user)
        .then(unwrapResult)
        .then(({ data: newUser }) => {
          const payload: EmailPayload = getEmailPayload({
            message,
            internalNote,
            formData,
            to_email: newUser.email,
            content_type,
            language,
          });
          // Sent email message
          this.props
            .sendEmail(payload)
            .then((response: SendEmailResponseT) => {
              // Show notification and remove draft if any.
              this.messageSent(response, formData.parentConversation);
            })
            .catch((e: Error) => {
              showErrorNotification(user.email as string, e);
              console.warn('Add user exception', e);
            });
        })
        .catch((e: Error) => {
          showErrorNotification(user.email as string, e);
          console.warn('Add user exception', e);
        });
    }
  };

  @boundMethod
  getTemplatesRenderContext() {
    const { formValues, organization, profile } = this.props;

    const user = {
      name: formValues?.toUser?.[0]?.user?.name || '',
      email: formValues?.toUser?.[0]?.user?.email || '',
    };

    const displayName = formValues?.toUser?.[0]?.user?.display_name;
    if (displayName !== undefined && displayName !== null) {
      user.name = displayName;
    }

    return {
      organization_name: sanitizeHtml(organization.name),
      requester_firstname: sanitizeHtml(
        user.name.substr(0, user.name.indexOf(' ')) || user.name,
      ),
      requester_fullname: sanitizeHtml(user.name),
      requester_email: sanitizeHtml(user.email),
      agent_firstname: sanitizeHtml(profile.given_name ?? ''),
      agent_lastname: sanitizeHtml(profile.family_name ?? ''),
      agent_fullname: sanitizeHtml(
        `${profile.given_name ?? ''} ${profile.family_name ?? ''}`,
      ),
      agent_displayname: sanitizeHtml(profile.name ?? ''),
      conversation_subject: sanitizeHtml(
        formValues && formValues.subject ? formValues.subject : '',
      ),
    };
  }

  @boundMethod
  renderInternalNoteTemplate(payload: { content: string }) {
    const { content } = payload;
    return this.props.templatesRender({
      content: content && typeof content === 'string' ? content : '',
      context: this.getTemplatesRenderContext(),
    });
  }

  renderMessageTemplate = (payload: { content: string; subject?: string }) => {
    const { content, subject } = payload;

    if (!content) {
      return Promise.reject('No content given');
    }

    if (content && typeof content === 'string' && content.trim() === '') {
      return Promise.reject('No content given');
    }

    const { formValues } = this.props;

    // Add subject replacement here.
    if (subject) {
      if (
        !formValues ||
        !formValues.subject ||
        (formValues && formValues.subject && formValues.subject.trim() === '')
      ) {
        this.subjectRender(subject, true);
      }
    }

    return this.props.templatesRender({
      context: this.getTemplatesRenderContext(),
      content: content && typeof content === 'string' ? content : '',
    });
  };

  subjectRender = (
    subject: string,
    rejectIfEmpty = false,
  ): Promise<any> | undefined => {
    if (!subject) {
      if (!rejectIfEmpty) return undefined;
      return Promise.reject('No subject given');
    }

    if (subject && typeof subject === 'string' && subject.trim() === '') {
      if (!rejectIfEmpty) return undefined;
      return Promise.reject('No subject given');
    }

    this.setState({ isSubjectLoading: true });
    this.props
      .templatesRender({
        context: this.getTemplatesRenderContext(),
        content: subject,
      })
      .then((resolve: any) => {
        this.setState({ isSubjectLoading: false });
        this.props.change('subject', resolve.data.rendered_content);
      })
      .catch((exception: any) => {
        this.setState({ isSubjectLoading: false });
        console.warn('templatesRender subject Exception', exception);
      });
    return undefined;
  };

  saveDraft = () => {
    const { satisfactionLanguage, cancelledSatisfaction } = this.state;
    const {
      formValues,
      initialValues,
      composeEmailSaveDraft,
      composeEmailCloseModal,
      destroy,
    } = this.props;
    const message = this.getMessageEditorText();
    const internalNote = this.getInternalNoteEditorText();

    if (formValues) {
      const draft: SaveDraftPayload = {};
      if (formValues.fromEmails) {
        draft.fromEmails = formValues.fromEmails;
      }
      if (
        formValues.toUser &&
        Array.isArray(formValues.toUser) &&
        formValues.toUser.length > 0 &&
        formValues.toUser[0].user
      ) {
        draft.toUser = formValues.toUser as EmailUser[];
      }
      if (
        formValues.cc &&
        Array.isArray(formValues.cc) &&
        formValues.cc.length > 0 &&
        formValues.cc[0].user
      ) {
        draft.cc = formValues.cc;
      }
      if (
        formValues.bcc &&
        Array.isArray(formValues.bcc) &&
        formValues.bcc.length > 0 &&
        formValues.bcc[0].user
      ) {
        draft.bcc = formValues.bcc;
      }
      if (formValues.subject) {
        draft.subject = formValues.subject;
      }
      if (message) {
        draft.message = message;
      }
      if (internalNote) {
        draft.internalNote = internalNote;
      }
      if (
        formValues.attachments &&
        Array.isArray(formValues.attachments) &&
        formValues.attachments.length > 0
      ) {
        const attachments: Array<any> = [];
        for (const attachment of formValues.attachments) {
          if (attachment) {
            attachments.push(attachment);
          }
        }
        draft.attachments = attachments;
      }
      if (formValues.tags) {
        draft.tags = formValues.tags;
      }
      if (formValues.linkConversationType) {
        draft.linkConversationType = formValues.linkConversationType;
      }
      if (formValues.messageContentType) {
        draft.messageContentType = formValues.messageContentType;
      }
      if (formValues.queue) {
        draft.queue = formValues.queue;
      }
      if (formValues.offer) {
        draft.offer = formValues.offer;
      }

      if (satisfactionLanguage) {
        draft.satisfactionLanguage = satisfactionLanguage;
      }

      if (cancelledSatisfaction) {
        draft.cancelledSatisfaction = cancelledSatisfaction;
      }

      // Parent Conversation for linked conversation.
      if (formValues.parentConversation) {
        draft.parentConversation = formValues.parentConversation;
      }

      composeEmailSaveDraft(draft)
        .then((response: any) => {
          if (this.props.initialValues?.timestamp) {
            // We need to delete the old draft.
            this.props.composeEmailRemoveDraft({
              timestamp: initialValues.timestamp,
            });
          }

          //this.props.notificationAdd(notificationOptions);
          destroy();
          composeEmailCloseModal();
          this.closeWindowPortal();
        })
        .catch((exception: any) => {
          console.warn('exception', exception);
        });
    }
  };

  minimizeModal = () => {
    const { composeEmailCloseModal, destroy, formValues, pristine } =
      this.props;
    if (this.state.showWindowPortal) {
      this.closeWindowPortal();
    }
    if (formValues && pristine === false) {
      this.saveDraft();
    } else {
      destroy();
      this.setValidationState({ touched: false, valid: false });
      composeEmailCloseModal();
    }
  };

  handleBeforeInput = () => {
    this.props.change('message', 'pristine');
  };

  handleMessageRichTextChange = () => {
    this.props.change('message', 'pristine');
  };

  handleInternalNoteRichTextChange = () => {
    this.props.change('internalNote', 'pristine');
  };

  handleFromEmailChange = (value: string) => {
    this.props
      .updateLastUsedEmailIntegration({
        key: 'lastUsedEmailIntegration',
        value,
      })
      .catch((e) => {
        console.log(e);
      });
  };

  setValidationState = (validation: ValidationState) => {
    this.setState({ validation });
  };

  toggleCC = () => {
    this.setState({ ccVisible: !this.state.ccVisible });
  };

  toggleBCC = () => {
    this.setState({ bccVisible: !this.state.bccVisible });
  };

  hideField = (targetName: string) => {
    if (targetName === 'Cc') {
      this.setState({ ccVisible: true });
    } else if (targetName === 'Bcc') {
      this.setState({ bccVisible: true });
    }
  };

  copyPasteParentConversationClickHandler = () => {
    const {
      metaConversation,
      formValues,
      hasFFWriteHTMLEmail,
      rewrittenHtmlEmailMessages,
    } = this.props;
    const linebreak = `<br /><br />`;
    const { transcript: transcriptRaw, attachments } = getMessagesTranscript(
      metaConversation,
      rewrittenHtmlEmailMessages,
      hasFFWriteHTMLEmail,
    );
    let transcript = `${linebreak}${linebreak}${transcriptRaw}`;

    // Set message from parent conversation and concat with already typed text.
    const currentText = this.getMessageEditorText(!hasFFWriteHTMLEmail);
    if (currentText) {
      transcript = `${linebreak} ${transcript}`;
    }
    this.setMessageEditorText(currentText + transcript, !hasFFWriteHTMLEmail);

    // Set subject (only if there not already typed a subject).
    if (metaConversation?.conversation?.attributes && formValues) {
      const subject = getSubject(
        metaConversation?.conversation?.attributes || null,
      );
      if (
        subject &&
        (!formValues ||
          !formValues.subject ||
          (formValues &&
            formValues.subject &&
            formValues.subject.trim() === ''))
      ) {
        this.props.change('subject', subject);
      }
    }

    // Add attachments from parent and concat with already added.
    if (attachments.length > 0) {
      if (formValues && formValues.attachments) {
        this.props.change(
          'attachments',
          attachments.concat(formValues.attachments),
        );
      } else {
        this.props.change('attachments', attachments);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore | the Event type comes from redux-form (I think)
  subjectOnBlurAction = (e: Event) => this.subjectRender(e.target.value);

  @boundMethod
  subjectOnKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      this.messageEditorRef.current?.focus();
    }
  }

  @boundMethod
  closeWindowPortal() {
    ComposeEmailHelper.setWindow(window);
    this.setState({
      showWindowPortal: false,
    });
  }

  @boundMethod
  toggleWindowPortal() {
    this.setState({
      showWindowPortal: !this.state.showWindowPortal,
    });
  }

  closeAndDestroyDraft = () => {
    const { destroy, composeEmailCloseModal } = this.props;
    if (this.state.showWindowPortal) {
      this.closeWindowPortal();
    }
    this.setCCsVisibility(true);
    destroy();
    composeEmailCloseModal();
  };

  setCCsVisibility = (ccState: boolean) => {
    this.setState({ ccVisible: ccState, bccVisible: ccState });
  };

  setCCsVisible = () => {
    this.setCCsVisibility(true);
  };

  @boundMethod
  handleEditorSubmit(): void {
    if (this.props.valid) {
      this.handleSaveClick(this.props.formValues);
    }
  }

  @boundMethod
  messageRichTextMount() {
    setTimeout(() => {
      this.setMessageEditorText(this.state.messageContent);
    });
  }

  @boundMethod
  messageRichTextUnmount(html: string) {
    if (this.props.ui.isModalOpen) {
      this.setState({
        messageContent: html,
      });
    }
  }

  @boundMethod
  internalNoteRichTextMount() {
    setTimeout(() => {
      this.setInternalNoteEditorText(this.state.internalNoteContent);
    });
  }

  @boundMethod
  internalNoteRichTextUnmount(html: string) {
    if (this.props.ui.isModalOpen) {
      this.setState({
        internalNoteContent: html,
      });
    }
  }

  @boundMethod
  renderElementItem(props: any) {
    return <UserItem {...props} />;
  }

  render() {
    let isLoading = false;
    let btnText = 'Send';
    let noEmailIntegration: JSX.Element | null = null;
    let linkedConversationLead: JSX.Element | null = null;
    let composeHeader = 'Compose new email';

    const {
      supportsPopoutCompose,
      showWindowPortal,
      cancelledSatisfaction,
      satisfactionLanguage,
    } = this.state;

    const {
      initialValues,
      meta,
      ui,
      fromEmails,
      formValues,
      metaConversation,
      profile,
      isUploadingAttachments,
      touch,
      handleSubmit,
      composeEmailCloseModal,
      destroy,
    } = this.props;

    const hasTagAccess = accessControl.hasAccess('conversation.tag');

    if (
      meta.send === 'loading' ||
      meta.findUser === 'loading' ||
      meta.getAllFrom === 'loading' ||
      meta.getPresignedUrl === 'loading' ||
      meta.addAttachments === 'loading' ||
      isUploadingAttachments
    ) {
      isLoading = true;
    }

    if (meta.send === 'loading') {
      btnText = 'Sending';
    } else if (meta.send === 'failed') {
      btnText = 'Failed - try again';
    } else if (isUploadingAttachments) {
      btnText = 'Uploading';
    }

    const listFromMails = (fromEmail: any, index: number) => {
      return (
        <option value={fromEmail.email_address} key={index}>
          {getEmailIntegrationLabel(fromEmail)}
        </option>
      );
    };

    if (fromEmails.length === 0 && meta.getAllFrom === 'succeeded') {
      noEmailIntegration = (
        <div className="well well-sm text-center">
          <h5>No email integration found</h5>
          {profile.is_admin === true && (
            <Link
              to="/settings/email"
              onClick={() => {
                destroy();
                composeEmailCloseModal();
              }}
            >
              Create one in email settings
            </Link>
          )}
        </div>
      );
    }

    if (initialValues && initialValues.parentConversation) {
      if (initialValues.linkConversationType === 'email_forward') {
        // Forward conversation.
        linkedConversationLead = (
          <div className="well well-sm text-center">
            <h5>
              Compose a transcript conversation of #
              {initialValues.parentConversation}
            </h5>
          </div>
        );
        composeHeader = `Compose transcript conversation of #${initialValues.parentConversation}`;
      } else if (initialValues.linkConversationType === 'side') {
        // Linked side conversation.
        linkedConversationLead = (
          <div className="well well-sm text-center">
            <h5>
              Compose a side conversation to #{initialValues.parentConversation}
            </h5>
            {!!metaConversation && (
              <Button onClick={this.copyPasteParentConversationClickHandler}>
                Copy & Paste from conversation #
                {initialValues.parentConversation}
              </Button>
            )}
          </div>
        );
        composeHeader = `Compose side conversation to #${initialValues.parentConversation}`;
      } else if (initialValues.linkConversationType === 'follow_up') {
        composeHeader = `Follow-up on Email for Chat #${initialValues.parentConversation} - with ${initialValues.toUser?.[0].user.email}`;
      }
    }

    const RenderComponent = showWindowPortal ? ComposeWindowPortal : Modal;
    const renderCompProps: any = {};

    if (supportsPopoutCompose && showWindowPortal) {
      renderCompProps.title = 'Compose email';
      renderCompProps.copyStyles = true;
      renderCompProps.onUnload = this.closeWindowPortal;
      renderCompProps.onBlock = this.closeWindowPortal;
      renderCompProps.name = '_modal';
      renderCompProps.url = '';

      renderCompProps.features = {
        width: 900,
        height: 710,
        directories: false,
        titlebar: false,
        toolbar: false,
        location: false,
        status: false,
        menubar: false,
        nodeIntegration: false,
        nativeWindowOpen: true,
      };
      renderCompProps.onOpen = (window) => {
        setTimeout(() => {
          this.messageEditorRef.current &&
            this.messageEditorRef.current.setText(this.state.messageContent);
          this.internalNoteEditorRef.current &&
            this.internalNoteEditorRef.current.setText(
              this.state.internalNoteContent,
            );
        }, 0);
        ComposeEmailHelper.setWindow(window);
      };
    } else {
      renderCompProps.isOpen = ui.isModalOpen;
      renderCompProps.onClose = this.minimizeModal;
    }

    return (
      <RenderComponent {...renderCompProps}>
        <div
          className={classnames({
            'compose-email-modal': true,
            'compose-email-modal--popout': showWindowPortal,
          })}
          id="compose-email-modal"
        >
          <form onSubmit={handleSubmit(this.handleSaveClick)}>
            <ModalHead
              title={composeHeader}
              onClose={this.minimizeModal}
              secondaryButton={
                supportsPopoutCompose
                  ? {
                      onClick: this.toggleWindowPortal,
                      tooltipContent: showWindowPortal
                        ? 'Show in main window'
                        : 'Open in new window',
                      icon: !showWindowPortal ? 'external-link' : 'compress',
                    }
                  : undefined
              }
            >
              {/* FIXME: [TEAMS-6670]  */}
              {/* {!this.props.pristine && (
                <label className={'unsaved-changes-label label label-danger'}>
                  Unsaved changes
                </label>
              )} */}
            </ModalHead>
            <ModalBody>
              {noEmailIntegration}
              {linkedConversationLead}
              <Field name="parentConversation" component="hidden" />
              <Field name="linkConversationType" component="hidden" />
              <FieldArray
                name="toUser"
                required={true}
                singleChoice={true}
                fieldLabel="To"
                touch={touch}
                component={ListUserEmailOptions as any}
                elementItem={this.renderElementItem}
                availableOptions={this.state.allEmailsUsers}
                status={this.props.meta.findUser}
                getfunction={this.props.composeEmailFindUser}
                placeholder="Search for email or name..."
                focus={!this.state.userFieldFilled}
              >
                <span className="cc-bcc-container">
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={this.toggleCC}
                  >
                    {this.state.ccVisible === true ? '+' : '-'}Cc
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    onClick={this.toggleBCC}
                  >
                    {this.state.bccVisible === true ? '+' : '-'}Bcc
                  </Button>
                </span>
              </FieldArray>

              {!this.state.ccVisible && (
                <FieldArray
                  name="cc"
                  required={false}
                  singleChoice={false}
                  fieldLabel="Cc"
                  touch={touch}
                  component={ListUserEmailOptions as any}
                  elementItem={this.renderElementItem}
                  hideField={this.hideField}
                  availableOptions={this.state.allEmailsUsers}
                  status={this.props.meta.findUser}
                  getfunction={this.props.composeEmailFindUser}
                  placeholder="Search for email or name..."
                />
              )}

              {!this.state.bccVisible && (
                <FieldArray
                  name="bcc"
                  required={false}
                  singleChoice={false}
                  fieldLabel="Bcc"
                  touch={touch}
                  component={ListUserEmailOptions as any}
                  elementItem={this.renderElementItem}
                  hideField={this.hideField}
                  availableOptions={this.state.allEmailsUsers}
                  status={this.props.meta.findUser}
                  getfunction={this.props.composeEmailFindUser}
                  placeholder="Search for email or name..."
                />
              )}

              <Field
                onChangeAction={this.handleFromEmailChange}
                displayName="From"
                required={true}
                name="fromEmails"
                component={SelectField}
              >
                {fromEmails.length > 1 && (
                  <option hidden value="">
                    {' '}
                    - Please select a from email
                  </option>
                )}
                {fromEmails.map(listFromMails)}
              </Field>
              <div className="subject-csat">
                <div className="subject">
                  <Field
                    displayName="Subject"
                    autoFocus={this.state.userFieldFilled}
                    required={true}
                    name="subject"
                    showSpinner={this.state.isSubjectLoading}
                    onBlurAction={this.subjectOnBlurAction}
                    onKeyDownAction={this.subjectOnKeyDown}
                    component={InputField}
                    type="text"
                    placeholder="Subject..."
                  />
                </div>
                {initialValues?.linkConversationType === 'follow_up' && (
                  <>
                    {cancelledSatisfaction && (
                      <div className="csat">
                        <SatisfactionDisabledButton text="Cancelled" />
                      </div>
                    )}
                    {!cancelledSatisfaction &&
                      formValues?.fromEmails != null && (
                        <div className="csat">
                          <SatisfactionButton
                            channel="email"
                            contactPoint={formValues.fromEmails}
                            selected={
                              satisfactionLanguage ??
                              satisfactionLanguageFallback
                            }
                            isCancellable={false}
                            onChange={(satisfactionLanguage) =>
                              this.setState({ satisfactionLanguage })
                            }
                            onCancel={() =>
                              this.setState({ cancelledSatisfaction: true })
                            }
                          />
                        </div>
                      )}
                  </>
                )}
              </div>
              <div
                className="richtext-wrap"
                data-testid="compose-richtext-message"
              >
                <RichTextField
                  label="Message"
                  placeholder="Type your message"
                  className="message-richtext-editor"
                  ref={this.messageEditorRef}
                  renderTemplate={this.renderMessageTemplate}
                  channel="email"
                  onChange={this.handleMessageRichTextChange}
                  validation={this.state.validation}
                  setValidationState={this.setValidationState}
                  attachments={formValues?.attachments ?? []}
                  onMount={this.messageRichTextMount}
                  onUnmount={this.messageRichTextUnmount}
                  attachmentsFields={
                    <FieldArray
                      name="attachments"
                      component={AttachmentsFieldList}
                    />
                  }
                  required
                  form={FORM_NAME}
                  editorWindow={ComposeEmailHelper.getWindow()}
                  onTriggerSend={this.handleEditorSubmit}
                  toolbarAddons={<EditorCopilotDropdown channel="email" />}
                />
              </div>
              <ToggleBox
                className={classnames('internal-note', {
                  'internal-note-open': !!formValues?.internalNoteOpen,
                })}
                headComponent={FieldToggleBoxHead}
                isOpen={!!formValues?.internalNoteOpen}
                title="Include an internal note"
                input={{
                  id: 'internalNoteOpen',
                  value: 'internalNoteOpen',
                  name: 'internalNoteOpen',
                }}
                onChange={() =>
                  ampli.followUpConversationModalInternalNoteAdded()
                }
              >
                <ToggleBoxBody>
                  <div className="richtext-wrap internal-note-wrap">
                    <RichTextField
                      className="internal-note-richtext-editor"
                      placeholder="Type your internal note"
                      label="Internal note"
                      channel="notes"
                      form={FORM_NAME}
                      ref={this.internalNoteEditorRef}
                      editorWindow={ComposeEmailHelper.getWindow()}
                      renderTemplate={this.renderInternalNoteTemplate}
                      onChange={this.handleInternalNoteRichTextChange}
                      onMount={this.internalNoteRichTextMount}
                      onUnmount={this.internalNoteRichTextUnmount}
                      onTriggerSend={this.handleEditorSubmit}
                      toolbarAddons={<EditorCopilotDropdown channel="email" />}
                    />
                  </div>
                </ToggleBoxBody>
              </ToggleBox>
              {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                <Tooltip
                  content="You don't have permissions to add tags"
                  dark
                  disabled={hasTagAccess}
                >
                  <FieldArray
                    name="tags"
                    component={TagsSelectFieldArray}
                    disabled={!hasTagAccess}
                  />
                </Tooltip>
              }
              <div className="queue-offer">
                <div className="queue">
                  <Field
                    className="queue"
                    name="queue"
                    component={QueueSelectField}
                  />
                </div>
                <div className="offer">
                  <label>
                    <span>Offer in queue</span>
                    <Field
                      name="offer"
                      type="checkbox"
                      component={FieldSwitch}
                      onChange={() =>
                        ampli.followUpConversationModalOfferInQueueAdded()
                      }
                    />
                  </label>
                </div>
              </div>
            </ModalBody>
            <ModalFoot
              cancelOnClick={this.closeAndDestroyDraft}
              cancelName="Cancel"
              confirmSecondaryOnClick={this.saveDraft}
              confirmSecondaryName="Save draft and close"
              confirmSecondaryProps={{
                className: styles.modalConfirmSecondaryButton,
              }}
              className={styles.modalButtons}
            >
              <Button
                type="submit"
                variant={meta.send === 'failed' ? 'danger' : 'primary'}
                disabled={isLoading}
                onClick={this.setCCsVisible}
              >
                {btnText}
              </Button>
            </ModalFoot>
          </form>
        </div>
      </RenderComponent>
    );
  }
}

export default reduxForm<
  ComposeEmailModalFormData,
  ComposeEmailModalProps,
  ComposeEmailModalErrorType
>({
  form: FORM_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  validate,
  asyncValidate,
})(ComposeEmailModal);
